import React, { useState, useEffect } from 'react';
import { Form, Input, Select, message } from 'antd';
import EditableDrawer from 'components/EditableDrawer';
import ZipcodeInput from 'components/ZipcodeInput';
import { usePickupRouteZipcodeConflictCheck } from '../hooks/usePickupRouteZipcodeConflictCheck';
import { usePickupRouteById } from '../hooks/usePickupRouteById';

interface RouteData {
  id: string;
  route: string;
  zipcodes: string;
  driverId: string;
  vehicleId: string;
  vehicleTypeName: string;
}

interface EditRouteDrawerProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (values: RouteData) => void;
  loading?: boolean;
  routeId: number;
  driverOptions: { label: string; value: string }[];
  vehicleOptions: { label: string; value: string }[];
  warehouseId?: number;
  // 添加完整的车辆数据
  vehicles: Array<{
    id: number;
    vehicleNumber: string;
    licensePlate: string;
    status: number;
    vehicleModelId: number;
    vehicleModelName: string;
    vehicleTypeName?: string;
  }>;
  // 添加司机数据
  drivers: Array<{
    accountId: string;
    driverName: string;
    status: number;
  }>;
}

const EditRouteDrawer: React.FC<EditRouteDrawerProps> = ({
  visible,
  onClose,
  onSubmit,
  loading = false,
  routeId,
  driverOptions,
  vehicleOptions,
  warehouseId,
  vehicles,
  drivers,
}) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [zipcodesData, setZipcodesData] = useState<string[]>([]);

  // 从数据库获取最新数据
  const { route: latestRoute, loading: routeLoading } = usePickupRouteById(routeId);

  // 使用zipcode冲突检查hook
  const { checkZipcodeConflict, loading: conflictCheckLoading } = usePickupRouteZipcodeConflictCheck();

  // 当从数据库获取到最新数据时，更新表单
  useEffect(() => {
    if (latestRoute && visible) {
      try {
        const zipcodesArray = JSON.parse(latestRoute.zipcodesListJson);
        const formattedZipcodes = Array.isArray(zipcodesArray) ? zipcodesArray.join(', ') : latestRoute.zipcodesListJson;
        setZipcodesData(Array.isArray(zipcodesArray) ? zipcodesArray : []);
        
        // 获取司机名称
        let driverName = '-';
        if (latestRoute.driverId) {
          const selectedDriver = drivers.find(d => d.accountId === latestRoute.driverId.toString());
          if (selectedDriver?.driverName) {
            driverName = selectedDriver.driverName;
          }
        }
        
        // 获取车辆名称
        let vehicleName = '-';
        if (latestRoute.vehicleId) {
          const selectedVehicle = vehicles.find(v => v.id === latestRoute.vehicleId);
          if (selectedVehicle) {
            vehicleName = `${selectedVehicle.vehicleNumber}-${selectedVehicle.licensePlate}`;
          }
        }
        
        form.setFieldsValue({
          id: latestRoute.id.toString(),
          route: latestRoute.routeName,
          zipcodes: formattedZipcodes,
          driverId: latestRoute.driverId.toString(),
          vehicleId: latestRoute.vehicleId?.toString() || '-',
          vehicleTypeName: latestRoute.vehicleTypeName || '-'
        });
      } catch (error) {
        console.error('Error parsing zipcodes from latest route:', error);
      }
    }
  }, [latestRoute, visible, form, vehicles, drivers]);

  // 处理Vehicle选择变化
  const handleVehicleChange = (vehicleId: string) => {
    if (!vehicleId) {
      form.setFieldValue('vehicleTypeName', undefined);
      return;
    }

    // 查找选中的车辆信息
    const selectedVehicle = vehicles.find(v => v.id.toString() === vehicleId);
    if (selectedVehicle) {
      const vehicleTypeName = selectedVehicle.vehicleTypeName;
      form.setFieldValue('vehicleTypeName', vehicleTypeName);
    }
  };

  // 提交表单
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const values = await form.validateFields();
      
      // 将zipcodes转换为JSON格式
      if (values.zipcodes) {
        const zipcodesArray = values.zipcodes
          .split(/[,\s\n\r\t;]+/)
          .map((zip: string) => zip.trim())
          .filter((zip: string) => zip.length > 0);
        
        if (zipcodesArray.length > 0) {
          // 检查zipcode冲突（包含warehouse pickup zone验证）
          const conflictResult = await checkZipcodeConflict(
            warehouseId || 0,
            zipcodesArray,
            routeId, // 排除当前编辑的路线
            values.route
          );
          
          if (conflictResult.hasConflict) {
            // 首先检查路线名称冲突
            if (conflictResult.hasNameConflict && conflictResult.conflictingRouteNames.length > 0) {
              message.error(`Route name "${values.route}" already exists, please use a different name.`);
              return;
            }
            
            // 检查warehouse pickup zone验证冲突
            if (conflictResult.hasWarehouseValidationConflict && conflictResult.invalidWarehouseZipcodes.length > 0) {
              const invalidZipcodes = conflictResult.invalidWarehouseZipcodes.join(', ');
              message.error(`The following zipcodes do not belong to the current hub's pickup zone: ${invalidZipcodes}. Please use only zipcodes that are configured in the hub's pickup zone.`);
              return;
            }
            
            // 然后检查邮编冲突
            if (conflictResult.conflictingZipcodes.length > 0) {
              let zipcodeErrorMessage = `The following zipcodes are already used by other routes, conflicting zipcodes: ${conflictResult.conflictingZipcodes.join(', ')}`;
              
              if (conflictResult.conflictDetails && conflictResult.conflictDetails.length > 0) {
                const conflictInfo = conflictResult.conflictDetails.map(detail => 
                  `zipcode ${detail.conflictingZipcode} is already used by route "${detail.routeName}"`
                ).join(', ');
                zipcodeErrorMessage += `\n(${conflictInfo})`;
              }
              
              message.error(zipcodeErrorMessage);
              return;
            }
            
            return;
          }
        }
        
        values.zipcodes = JSON.stringify(zipcodesArray);
      } else {
        values.zipcodes = JSON.stringify([]);
      }
      
      await onSubmit(values);
    } catch (error) {
      console.error('Form validation failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 检查zipcode冲突的函数
  const checkZipcodeConflicts = async (zipcodes: string[], routeName: string) => {
    if (!warehouseId || zipcodes.length === 0) {
      return {
        hasConflict: false,
        conflictingRoutes: [],
        conflictingZipcodes: [],
        conflictDetails: [],
        hasNameConflict: false,
        conflictingRouteNames: []
      };
    }

    try {
      const conflictResult = await checkZipcodeConflict(
        warehouseId,
        zipcodes,
        routeId, // 编辑模式需要excludeRouteId
        routeName
      );

      if (conflictResult.error) {
        console.error('Error checking zipcode conflicts:', conflictResult.error);
        message.error('检查邮编冲突时发生错误');
        return {
          hasConflict: false,
          conflictingRoutes: [],
          conflictingZipcodes: [],
          conflictDetails: [],
          hasNameConflict: false,
          conflictingRouteNames: []
        };
      }

      return conflictResult;
    } catch (error) {
      console.error('Error in checkZipcodeConflicts:', error);
      message.error('检查邮编冲突时发生错误');
      return {
        hasConflict: false,
        conflictingRoutes: [],
        conflictingZipcodes: [],
        conflictDetails: [],
        hasNameConflict: false,
        conflictingRouteNames: []
      };
    }
  };

  if (routeLoading) {
    return (
      <EditableDrawer
        title="Edit Route"
        visible={visible}
        onClose={onClose}
        width={550}
      >
        <div className="text-center py-8">Loading route data...</div>
      </EditableDrawer>
    );
  }

  return (
    <EditableDrawer
      title="Edit Route"
      visible={visible}
      onClose={onClose}
      onSubmit={handleSubmit}
      confirmText={isSubmitting ? "Updating..." : "Update"}
      confirmDisabled={isSubmitting || conflictCheckLoading}
      width={550}
    >
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
      >
        <Form.Item name="id" label="ID">
          <Input disabled />
        </Form.Item>

        <Form.Item 
          name="route" 
          label="Route Name" 
          rules={[
            { required: true, message: 'Please input route name' },
            { min: 2, message: 'Route name must be at least 2 characters' },
            { max: 50, message: 'Route name must not exceed 50 characters' }
          ]}
        >
          <Input 
            placeholder="Enter route name" 
          />
        </Form.Item>

        <Form.Item
          name="zipcodes"
          label="Zipcodes"
          rules={[
            { required: true, message: 'Please input zipcodes' }
          ]}
        >
          <ZipcodeInput
            data={{ zipcodes: zipcodesData }}
            isEditing={true}
            onChange={(data) => {
              setZipcodesData(data.zipcodes);
              const zipcodesString = data.zipcodes.join(', ');
              form.setFieldValue('zipcodes', zipcodesString);
            }}
            placeholder="Enter zipcodes separated by commas, spaces, or new lines (e.g., 12345, 67890, 54321)"
            rows={4}
          />
        </Form.Item>

        <Form.Item 
          name="driverId" 
          label="Driver" 
          rules={[{ required: true, message: 'Please select a driver' }]}
        >
          <Select 
            options={driverOptions}
            placeholder="Select a driver"
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            allowClear
          />
        </Form.Item>

        <Form.Item 
          name="vehicleId" 
          label="Vehicle" 
          rules={[{ required: true, message: 'Please select a vehicle' }]}
        >
          <Select
            options={vehicleOptions}
            placeholder="Select a vehicle"
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            notFoundContent="No vehicles found"
            allowClear
            onChange={handleVehicleChange}
          />
        </Form.Item>

        <Form.Item
          name="vehicleTypeName"
          label="Vehicle Model"
        >
          <Input 
            placeholder="Auto-assigned based on vehicle selection"
            disabled
          />
        </Form.Item>
      </Form>
    </EditableDrawer>
  );
};

export default EditRouteDrawer;
